import { useTranslation } from 'react-i18next';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { PageHeader } from '../components/PageHeader';
import { ScanBlocks } from '../components/ScanBlocks';
import { DualScopeScore } from '../components/DualScopeScore';
import { SetupChecklist } from '../components/SetupChecklist';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { useTriggerScan } from '../hooks/useTriggerScan';
import { computeScoreBreakdown } from '../domain/scanEngine';
import { ArrowSync20Regular } from '@fluentui/react-icons';

export function DashboardPage() {
  const { t, i18n }      = useTranslation();
  const { tenant }       = useActiveTenant();
  const latestScan       = useLatestScan(tenant.id);
  const triggerScan      = useTriggerScan();
  const isScanning       = triggerScan.isPending;

  const onRescan = () => {
    triggerScan.mutate({ tenant, scanType: 'full' });
  };

  // Score seulement s'il y a un scan ; on ne rend le tableau de bord complet que
  // s'il existe au moins un contrôle évalué (sinon on reste sur les placeholders).
  const score = latestScan ? computeScoreBreakdown(latestScan) : null;
  const evaluatedTotal = score?.counts.total ?? 0;
  const scanRunning = !!latestScan && evaluatedTotal === 0; // scan en cours / vide
  const hasData = !!latestScan && !!score && evaluatedTotal > 0;

  // Pas de données exploitables (aucun scan OU scan en cours sans résultats) :
  // on affiche les placeholders M365/Azure au lieu d'un tableau de score vide.
  if (!hasData || !latestScan || !score) {
    return (
      <div>
        <SetupChecklist />
        <ScanBlocks />
        <PageHeader
          title={t('dashboard.title', { tenant: tenant.name })}
          description={scanRunning
            ? t('dashboard.scanRunning', 'Audit en cours — les résultats s’afficheront à la fin du scan.')
            : t('dashboard.noScans')}
          actions={
            <Button appearance="primary" icon={<ArrowSync20Regular />} onClick={onRescan} disabled={isScanning || scanRunning}>
              {(isScanning || scanRunning) ? t('liveScanPanel.scanning') : t('dashboard.actions.rescan')}
            </Button>
          }
        />
        {!scanRunning && (
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>{t('dashboard.noScansBanner')}</MessageBarTitle>
              {t('dashboard.noScansBody', { tenant: tenant.name })}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    );
  }

  if (typeof latestScan.globalScore === 'number') {
    score.globalScore = latestScan.globalScore;
  }

  return (
    <div>
      {/* Checklist setup (4 etapes) - se cache quand tout est OK. */}
      <SetupChecklist />

      {/* Deux blocs : Microsoft 365 + Microsoft Azure (lancement + resume). */}
      <ScanBlocks />

      <PageHeader
        title={t('dashboard.title', { tenant: tenant.name })}
        description={t('dashboard.lastScan', {
          date:  new Date(latestScan.startedAt).toLocaleString(i18n.language),
          count: score.counts.total,
        })}
      />

      {/* Score global combine (M365 + Azure = Global) + score par famille en
          deux colonnes alignees sous chaque perimetre. */}
      <DualScopeScore m365Scan={latestScan} />
    </div>
  );
}
